import {
  BILLING_LOG_DATA_ENDPOINT,
  BILLING_LOG_ENDPOINT,
  BILLING_PDF_ENDPOINT,
  ORG_BILLING_ENDPOINT_LOG
} from "../common/constants"
import { api } from "../common/utils"
import { getQueryString } from "../common/utils/axiosApi"
import {
  AllOrgsBillingData,
  BillingEmpty,
  BillingLogListResponse,
  BillingLogs,
  BillingPdf,
  BillingTrendResponse
} from "../types"

/**
 * Fetch the system usage for an organization.
 * @param orgId The organization ID.
 * @param month Optional month parameter.
 * @returns System usage data for the organization.
 */
const fetchOrgSystemUsage = async (
  orgId: string | number,
  month?: string
): Promise<BillingLogs> => {
  try {
    const monthParam = month ? `&month=${month}` : ""
    const response = await api.get<BillingLogs | BillingEmpty>(
      `${ORG_BILLING_ENDPOINT_LOG}?org_id=${orgId}${monthParam}`
    )

    if (!response) {
      throw new Error(`Failed to fetch system usage for org ID ${orgId}`)
    }

    if ("status" in response.data && response.data.status === "error") {
      return []
    }

    if (Array.isArray(response.data)) {
      return response.data
    } else {
      throw new Error(`Unexpected response data for org ID ${orgId}`)
    }
  } catch (error) {
    console.error(`Error fetching system usage for org ID ${orgId}:`, error)
    throw error
  }
}

/**
 * Fetch system usage data for all organizations.
 * @param orgs Array of organizations.
 * @param month Optional month parameter.
 * @returns A mapping of organization IDs to system usage data.
 */
const fetchAllSystemUsage = async (
  orgs: { id: string }[],
  month?: string
): Promise<AllOrgsBillingData> => {
  const usageResponses = await Promise.all(
    orgs.map(async org => {
      const data = await fetchOrgSystemUsage(org.id, month)
      return { orgId: org.id, data }
    })
  )

  const usageMap: { [orgId: string]: BillingLogs } = {}
  usageResponses.forEach(({ orgId, data }) => {
    usageMap[orgId] = data
  })

  return usageMap
}

/**
 * Fetch billing PDF for an invoice of an organization.
 * @param org_id Organization ID.
 * @param invoice Invoice ID.
 * @returns BillingPdf
 */
const fetchBillingPdf = async (
  org_id: number,
  invoice: string
): Promise<BillingPdf> => {
  try {
    const response = await api.get<BillingPdf>(
      `${BILLING_PDF_ENDPOINT}?org_id=${org_id}&invoice=${invoice}`
    )

    return response.data
  } catch (error) {
    throw error
  }
}
const fetchBillingLogs = async (params: {
  month?: string
  status?: string
  org_name?: string
  exclude_zero?: boolean
  sort?: string
  sort_dir?: string
  page?: number
  limit?: number
}): Promise<BillingLogListResponse> => {
  const response = await api.get<BillingLogListResponse>(
    `${BILLING_LOG_ENDPOINT}${getQueryString(params)}`
  )
  return response.data
}

const fetchBillingTrend = async (params: {
  interval?: string
  from?: number
  to?: number
  status?: string
  org_name?: string
  exclude_zero?: boolean
}): Promise<BillingTrendResponse> => {
  const response = await api.get<BillingTrendResponse>(
    `${BILLING_LOG_DATA_ENDPOINT}${getQueryString(params)}`
  )
  return response.data
}

const billing = {
  fetchOrgSystemUsage,
  fetchAllSystemUsage,
  fetchBillingLogs,
  fetchBillingTrend,
  fetchBillingPdf
}

export default billing
